import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Switch,
  Typography,
} from 'antd';
import {
  CloseOutlined,
  EditOutlined,
  EyeOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiErrorMessage } from '@api/server';
import MarkdownView from '../markdown/markdown-view';
import {
  CHECKER_VID_LABELS,
  createAgent,
  getAgent,
  updateAgent,
} from './agents';

const CHECKER_VID_OPTIONS = Object.entries(CHECKER_VID_LABELS).map(([value, label]) => ({
  value: Number(value),
  label,
}));

const { TextArea } = Input;
const { Text } = Typography;
const AGENT_DRAWER_WIDTH_KEY = 'botva.agents.drawerWidth';
const MIN_AGENT_DRAWER_WIDTH = 560;
const DEFAULT_AGENT_DRAWER_WIDTH = 720;
const DRAWER_VIEWPORT_GAP = 48;

const getInitialDrawerWidth = () => {
  const savedWidth = Number(localStorage.getItem(AGENT_DRAWER_WIDTH_KEY));
  if (Number.isFinite(savedWidth) && savedWidth >= MIN_AGENT_DRAWER_WIDTH) {
    return savedWidth;
  }

  return DEFAULT_AGENT_DRAWER_WIDTH;
};

const clampDrawerWidth = (width) => {
  const maxWidth = Math.max(
    MIN_AGENT_DRAWER_WIDTH,
    window.innerWidth - DRAWER_VIEWPORT_GAP,
  );

  return Math.min(Math.max(width, MIN_AGENT_DRAWER_WIDTH), maxWidth);
};

const MarkdownPreviewToggle = ({ isPreview, onToggle }) => (
  <Button
    className="markdown-editor-preview-toggle"
    type="text"
    size="small"
    icon={isPreview ? <EditOutlined /> : <EyeOutlined />}
    aria-label={isPreview ? 'Редактирование' : 'Предпросмотр'}
    title={isPreview ? 'Редактирование' : 'Предпросмотр'}
    onClick={(event) => {
      event.stopPropagation();
      onToggle();
    }}
  />
);

const MarkdownField = ({ name, placeholder, minRows = 2, rules, isPreview }) => (
  <Form.Item noStyle shouldUpdate={(previousValues, nextValues) => (
    previousValues[name] !== nextValues[name]
  )}
  >
    {({ getFieldValue }) => {
      const value = getFieldValue(name) || '';

      return (
        <div className="markdown-editor">
          <div hidden={isPreview}>
            <Form.Item name={name} rules={rules} noStyle>
              <TextArea
                key={isPreview ? 'preview' : 'edit'}
                className="markdown-editor-textarea"
                placeholder={placeholder}
                autoSize={{ minRows }}
              />
            </Form.Item>
          </div>

          {isPreview && (
            value ? (
              <MarkdownView>{value}</MarkdownView>
            ) : (
              <Text type="secondary">Markdown-текст пока пуст</Text>
            )
          )}
        </div>
      );
    }}
  </Form.Item>
);

const Agent = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(getInitialDrawerWidth);
  const [previewFields, setPreviewFields] = useState({
    description: false,
    prompt: false,
  });

  const togglePreviewField = useCallback((field) => {
    setPreviewFields((current) => ({
      ...current,
      [field]: !current[field],
    }));
  }, []);

  const agentId = useMemo(() => {
    if (!id) {
      return null;
    }

    const nextId = Number(id);
    return Number.isInteger(nextId) && nextId > 0 ? nextId : null;
  }, [id]);
  const isNewAgent = !id;
  const title = isNewAgent ? 'Новый агент' : `Агент #${agentId || ''}`;
  const closeDrawer = useCallback(() => {
    navigate('/agents');
  }, [navigate]);

  const loadAgent = useCallback(async () => {
    if (isNewAgent) {
      form.setFieldsValue({
        name: '',
        description: '',
        prompt: '',
        active: true,
        maxtokenqty: 1024,
        temperature: 1,
        code: '',
        checkervid: 0,
        baseurl: '',
        model: '',
      });
      return;
    }

    if (!agentId) {
      setError('Некорректный идентификатор агента.');
      return;
    }

    setError('');
    setIsLoading(true);
    try {
      const agent = await getAgent(agentId);
      form.setFieldsValue({
        name: agent.name,
        description: agent.description || '',
        prompt: agent.prompt || '',
        active: Boolean(agent.active),
        maxtokenqty: Number(agent.maxtokenqty),
        temperature: Number(agent.temperature),
        code: agent.code || '',
        checkervid: Number(agent.checkervid) || 0,
        baseurl: agent.baseurl || '',
        model: agent.model || '',
      });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, [agentId, form, isNewAgent]);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  const handleSave = async (values) => {
    setError('');
    setIsSaving(true);
    try {
      const payload = {
        ...values,
        name: values.name?.trim(),
        description: values.description?.trim() || null,
        prompt: values.prompt?.trim(),
        code: values.code?.trim().toLowerCase(),
        baseurl: values.baseurl?.trim() || null,
        model: values.model?.trim() || null,
      };

      if (isNewAgent) {
        await createAgent(payload);
        navigate('/agents', { state: { agentsRefreshKey: Date.now() } });
        return;
      }

      await updateAgent({
        id: agentId,
        ...payload,
      });
      navigate('/agents', { state: { agentsRefreshKey: Date.now() } });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  const handleResizeStart = useCallback((event) => {
    event.preventDefault();

    const handleMouseMove = (moveEvent) => {
      const nextWidth = clampDrawerWidth(window.innerWidth - moveEvent.clientX);
      setDrawerWidth(nextWidth);
      localStorage.setItem(AGENT_DRAWER_WIDTH_KEY, String(nextWidth));
    };

    const handleMouseUp = () => {
      document.body.classList.remove('drawer-resizing');
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    document.body.classList.add('drawer-resizing');
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, []);

  return (
    <Drawer
      className="user-drawer agent-resizable-drawer"
      title={title}
      placement="right"
      width={drawerWidth}
      open
      closable={false}
      onClose={closeDrawer}
      destroyOnHidden
      extra={(
        <Button
          type="text"
          icon={<CloseOutlined />}
          aria-label="Закрыть"
          onClick={closeDrawer}
        />
      )}
      footer={(
        <div className="user-drawer-footer">
          <Button type="primary" icon={<SaveOutlined />} loading={isSaving} onClick={() => form.submit()}>
            Сохранить
          </Button>
          <Button onClick={closeDrawer}>Отмена</Button>
        </div>
      )}
    >
      <div
        className="agent-drawer-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Изменить ширину окна агента"
        onMouseDown={handleResizeStart}
      />

      {error && (
        <Alert className="user-alert" type="error" message={error} showIcon />
      )}

      <Spin spinning={isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          requiredMark={false}
          initialValues={{
            name: '',
            description: '',
            prompt: '',
            active: true,
            maxtokenqty: 1024,
            temperature: 1,
            code: '',
            checkervid: 0,
            baseurl: '',
            model: '',
          }}
        >
          <Form.Item
            label="Название"
            name="name"
            rules={[
              { required: true, message: 'Введите название агента.' },
              { max: 255, message: 'Название должно быть не длиннее 255 символов.' },
            ]}
          >
            <Input placeholder="Название агента" autoFocus />
          </Form.Item>

          <Form.Item
            label="Code"
            name="code"
            extra="Используется в [TRANSFER: code] и хранится в messages.operator."
            rules={[
              { required: true, message: 'Введите код агента.' },
              { max: 80, message: 'Code должен быть не длиннее 80 символов.' },
            ]}
          >
            <Input placeholder="router" />
          </Form.Item>

          <Collapse
            className="agent-text-collapse"
            defaultActiveKey={['prompt']}
            items={[
              {
                key: 'description',
                label: 'Описание',
                forceRender: true,
                extra: (
                  <MarkdownPreviewToggle
                    isPreview={previewFields.description}
                    onToggle={() => togglePreviewField('description')}
                  />
                ),
                children: (
                  <MarkdownField
                    name="description"
                    placeholder="Описание агента в Markdown"
                    isPreview={previewFields.description}
                  />
                ),
              },
              {
                key: 'prompt',
                label: 'Системный промпт',
                forceRender: true,
                extra: (
                  <MarkdownPreviewToggle
                    isPreview={previewFields.prompt}
                    onToggle={() => togglePreviewField('prompt')}
                  />
                ),
                children: (
                  <MarkdownField
                    name="prompt"
                    placeholder="Системный промпт в Markdown"
                    rules={[{ required: true, message: 'Введите системный промпт.' }]}
                    isPreview={previewFields.prompt}
                  />
                ),
              },
            ]}
          />

          <div className="form-row">
            <Form.Item
              label="Максимум токенов"
              name="maxtokenqty"
              rules={[{ required: true, message: 'Введите максимальное количество токенов.' }]}
            >
              <InputNumber min={1} precision={0} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="Температура"
              name="temperature"
              rules={[{ required: true, message: 'Введите температуру.' }]}
            >
              <InputNumber min={0} max={2} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              label="Checker"
              name="checkervid"
              rules={[{ required: true, message: 'Выберите checker.' }]}
            >
              <Select options={CHECKER_VID_OPTIONS} />
            </Form.Item>

            <Form.Item label="Активен" name="active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>

          <Form.Item
            label="Base URL"
            name="baseurl"
            extra="Если задан, используется вместо общего base_url из конфига при обращении к LLM."
          >
            <Input placeholder="https://example.com/v1" allowClear />
          </Form.Item>

          <Form.Item
            label="Model"
            name="model"
            extra="Если задана, используется вместо общей model из конфига при обращении к LLM."
          >
            <Input placeholder="gpt-4o" allowClear />
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
};

export default Agent;
